import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  buildNativeOAuthDeepLink,
  isNativeHandoffRequest,
} from '../utils/oauthNativeHandoff';
import { Button } from '../components/ui/button';
import { t } from '../locales';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { finalizeOAuthToken, checkAuth } = useAuth();
  const processedKeyRef = useRef('');
  const [showNativeFallback, setShowNativeFallback] = useState(false);
  const tokenForRetryRef = useRef('');

  useEffect(() => {
    const runKey = `${location.pathname}|${location.search}|${location.hash}`;
    if (processedKeyRef.current === runKey) return;
    processedKeyRef.current = runKey;

    const processAuth = async () => {
      const hash = location.hash ? location.hash.substring(1) : '';
      const params = new URLSearchParams(hash);
      const searchParams = new URLSearchParams(location.search || '');

      if (searchParams.get('error') === 'access_denied') {
        toast.error(t('auth.callback.cancelled'));
        navigate('/login', { replace: true });
        return;
      }

      const token = params.get('token');
      const nativeHandoff = token && isNativeHandoffRequest(location.search || '');

      if (nativeHandoff) {
        tokenForRetryRef.current = token;
        window.location.replace(buildNativeOAuthDeepLink(token));
        window.setTimeout(() => setShowNativeFallback(true), 5000);
        return;
      }

      if (token) {
        const ok = await finalizeOAuthToken(token);
        if (!ok) {
          toast.error(t('auth.callback.sessionInvalid'));
          navigate('/login', { replace: true });
          return;
        }
        toast.success(t('auth.callback.loginOk'));
        navigate('/dashboard', { replace: true });
        return;
      }

      const okCookie = await checkAuth({ force: true });
      if (okCookie) {
        toast.success(t('auth.callback.loginOk'));
        navigate('/dashboard', { replace: true });
        return;
      }

      toast.error(t('auth.callback.sessionNotFound'));
      navigate('/login', { replace: true });
    };

    processAuth();
  }, [location.pathname, location.search, location.hash, navigate, finalizeOAuthToken, checkAuth]);

  const retryDeepLink = () => {
    const tok = tokenForRetryRef.current;
    if (tok) window.location.href = buildNativeOAuthDeepLink(tok);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="text-center max-w-sm">
        {!showNativeFallback ? (
          <>
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500" />
            <p className="mt-4 text-muted-foreground">{t('auth.callback.completing')}</p>
          </>
        ) : (
          <>
            <p className="text-muted-foreground mb-4">{t('auth.callback.nativeFallback')}</p>
            <div className="flex flex-col gap-2">
              <Button type="button" className="btn-primary w-full" onClick={retryDeepLink}>
                {t('auth.callback.openApp')}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full border-white/20"
                onClick={() => navigate('/login', { replace: true })}
              >
                {t('auth.callback.goLogin')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
